/**
 * UIUtils - React 运行时 UI 工具
 *
 * 提供命令式的 UI 操作，如打开 Drawer、Modal 等
 * 使用 React 18 的 createRoot API 动态挂载组件
 */

import React, { Suspense } from "react";
import { createRoot, Root } from "react-dom/client";
import { Drawer, Modal, Spin, ConfigProvider, message } from "antd";
import type {
  DrawerProps as AntDrawerProps,
  ModalProps as AntModalProps,
} from "antd";
import zhCN from "antd/locale/zh_CN";
import { getCoreConfig } from "@nuwatop/code-canvas-core";

// Antd 与 React 19 类型兼容：作为 JSX 组件使用
const AntDrawer = Drawer as React.ComponentType<any>;
const AntModal = Modal as React.ComponentType<any>;
const AntSpin = Spin as React.ComponentType<any>;
const AntConfigProvider = ConfigProvider as React.ComponentType<any>;

/**
 * Drawer 配置选项
 * @see antd Drawer 使用 size 替代已废弃的 width（size 支持 'default' | 'large' | number）
 */
export interface DrawerOptions
  extends Omit<AntDrawerProps, "open" | "onClose" | "size"> {
  /** 标题 */
  title?: React.ReactNode;
  /** 宽度/尺寸：'default' | 'large' 或具体数值如 600 */
  size?: "default" | "large" | number;
  /** 位置 */
  placement?: "left" | "right" | "top" | "bottom";
  /** 要渲染的组件（直接传入组件引用） */
  component?: React.ComponentType<any>;
  /** 组件名称（通过 componentResolver 解析） */
  componentName?: string;
  /** 传递给组件的 props */
  componentProps?: Record<string, any>;
  /** 关闭回调 */
  onClose?: () => void;
  /** 销毁后回调 */
  afterClose?: () => void;
}

/**
 * Modal 配置选项
 */
export interface ModalOptions
  extends Omit<AntModalProps, "open" | "onCancel" | "onOk"> {
  /** 标题 */
  title?: React.ReactNode;
  /** 宽度 */
  width?: number | string;
  /** 要渲染的组件 */
  component?: React.ComponentType<any>;
  /** 组件名称（通过 componentResolver 解析） */
  componentName?: string;
  /** 传递给组件的 props */
  componentProps?: Record<string, any>;
  /** 关闭回调 */
  onClose?: () => void;
  /** 确认回调 */
  onOk?: () => void | Promise<void>;
  /** 是否显示底部按钮 */
  footer?: React.ReactNode | null;
}

/**
 * Drawer 实例接口
 */
interface DrawerInstance {
  id: string;
  root: Root;
  mountNode: HTMLDivElement;
  cleanup: () => void;
}

/**
 * Drawer 管理器 - 管理所有打开的 Drawer 实例
 */
class DrawerManager {
  private drawers = new Map<string, DrawerInstance>();
  private isSetup = false;

  /**
   * 添加 Drawer 实例
   */
  add(instance: DrawerInstance) {
    this.drawers.set(instance.id, instance);
    this.setupGlobalCleanup();
  }

  /**
   * 移除 Drawer 实例
   */
  remove(id: string) {
    this.drawers.delete(id);
  }

  /**
   * 清理所有 Drawer
   */
  cleanupAll() {
    this.drawers.forEach((instance) => {
      try {
        instance.cleanup();
      } catch (error) {
        console.error("Failed to cleanup drawer:", error);
      }
    });
    this.drawers.clear();
  }

  /**
   * 获取当前打开的 Drawer 数量
   */
  get count() {
    return this.drawers.size;
  }

  /**
   * 设置全局清理监听
   */
  private setupGlobalCleanup() {
    if (this.isSetup || typeof window === "undefined") return;

    this.isSetup = true;

    // 页面卸载时清理所有 Drawer
    window.addEventListener("beforeunload", () => {
      this.cleanupAll();
    });

    // 开发环境下的内存泄漏检测
    if (typeof import.meta !== "undefined" && import.meta.env?.DEV) {
      setInterval(() => {
        if (this.count > 5) {
          console.warn(
            `Potential memory leak: ${this.count} drawers are currently open.`
          );
        }
      }, 30000);
    }
  }
}

const drawerManager = new DrawerManager();

/**
 * 生成唯一 ID
 */
const generateId = () =>
  `drawer-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

/**
 * Drawer 包装组件
 */
const DrawerWrapper: React.FC<{
  options: DrawerOptions;
  onClose: () => void;
  Component: React.ComponentType<any> | null;
}> = ({ options, onClose, Component }) => {
  const [open, setOpen] = React.useState(true);
  const config = getCoreConfig();

  const handleClose = () => {
    setOpen(false);
    options.onClose?.();
    // 延迟清理，等待动画完成
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const { component, componentName, componentProps, ...drawerProps } = options;

  return (
    <AntConfigProvider locale={zhCN} theme={config.theme}>
      <AntDrawer {...(drawerProps as any)} open={open} onClose={handleClose} destroyOnHidden>
        {Component ? (
          <Suspense
            fallback={
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  padding: 40,
                }}
              >
                <AntSpin />
              </div>
            }
          >
            <Component {...(componentProps || {})} onClose={handleClose} />
          </Suspense>
        ) : (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <AntSpin tip="加载中..." />
          </div>
        )}
      </AntDrawer>
    </AntConfigProvider>
  );
};

/**
 * Modal 包装组件
 */
const ModalWrapper: React.FC<{
  options: ModalOptions;
  onClose: () => void;
  Component: React.ComponentType<any> | null;
}> = ({ options, onClose, Component }) => {
  const [open, setOpen] = React.useState(true);
  const [confirmLoading, setConfirmLoading] = React.useState(false);
  const config = getCoreConfig();

  const handleClose = () => {
    setOpen(false);
    options.onClose?.();
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const handleOk = async () => {
    if (options.onOk) {
      setConfirmLoading(true);
      try {
        await options.onOk();
        handleClose();
      } catch (error) {
        console.error("Modal onOk error:", error);
      } finally {
        setConfirmLoading(false);
      }
    } else {
      handleClose();
    }
  };

  const { component, componentName, componentProps, onOk, ...modalProps } =
    options;

  return (
    <AntConfigProvider locale={zhCN} theme={config.theme}>
      <AntModal
        {...modalProps}
        open={open}
        onCancel={handleClose}
        onOk={handleOk}
        confirmLoading={confirmLoading}
        destroyOnHidden
      >
        {Component ? (
          <Suspense
            fallback={
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  padding: 40,
                }}
              >
                <AntSpin />
              </div>
            }
          >
            <Component {...(componentProps || {})} onClose={handleClose} />
          </Suspense>
        ) : (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 100,
            }}
          >
            <AntSpin tip="加载中..." />
          </div>
        )}
      </AntModal>
    </AntConfigProvider>
  );
};

/**
 * 显示 Drawer
 * @param options Drawer 配置选项
 */
export function showDrawer(options: DrawerOptions): () => void {
  const id = generateId();

  // 创建挂载点
  const mountNode = document.createElement("div");
  mountNode.setAttribute("data-drawer-container", "true");
  mountNode.setAttribute("data-drawer-id", id);
  document.body.appendChild(mountNode);

  // 创建 React Root
  const root = createRoot(mountNode);

  // 清理函数
  const cleanup = () => {
    try {
      drawerManager.remove(id);
      root.unmount();
      mountNode.remove();
      options.afterClose?.();
    } catch (error) {
      console.error("Drawer cleanup failed:", error);
    }
  };

  // 添加到管理器
  drawerManager.add({ id, root, mountNode, cleanup });

  // 解析组件
  let Component: React.ComponentType<any> | null = options.component || null;

  if (!Component && options.componentName) {
    const config = getCoreConfig();
    if (config.componentResolver) {
      Component = config.componentResolver(options.componentName);
    } else {
      console.warn(
        "[UIUtils.showDrawer] componentResolver not configured. Cannot resolve component:",
        options.componentName
      );
    }
  }

  // 渲染 Drawer
  root.render(
    <DrawerWrapper options={options} onClose={cleanup} Component={Component} />
  );

  // 返回关闭函数
  return cleanup;
}

/**
 * 显示 Modal
 * @param options Modal 配置选项
 */
export function showModal(options: ModalOptions): () => void {
  const id = generateId();

  // 创建挂载点
  const mountNode = document.createElement("div");
  mountNode.setAttribute("data-modal-container", "true");
  mountNode.setAttribute("data-modal-id", id);
  document.body.appendChild(mountNode);

  // 创建 React Root
  const root = createRoot(mountNode);

  // 清理函数
  const cleanup = () => {
    try {
      root.unmount();
      mountNode.remove();
    } catch (error) {
      console.error("Modal cleanup failed:", error);
    }
  };

  // 解析组件
  let Component: React.ComponentType<any> | null = options.component || null;

  if (!Component && options.componentName) {
    const config = getCoreConfig();
    if (config.componentResolver) {
      Component = config.componentResolver(options.componentName);
    } else {
      console.warn(
        "[UIUtils.showModal] componentResolver not configured. Cannot resolve component:",
        options.componentName
      );
    }
  }

  // 渲染 Modal
  root.render(
    <ModalWrapper options={options} onClose={cleanup} Component={Component} />
  );

  // 返回关闭函数
  return cleanup;
}

/**
 * 清理所有打开的 Drawer
 */
export function cleanupAllDrawers(): void {
  drawerManager.cleanupAll();
}

/**
 * 获取当前打开的 Drawer 数量
 */
export function getDrawerCount(): number {
  return drawerManager.count;
}

/**
 * 显示消息
 * @param level 消息级别: 'success' | 'error' | 'info' | 'warning' | 'loading'
 * @param content 消息内容
 * @param duration 持续时间（秒）
 * @param onClose 关闭回调
 */
export function msg(
  level: "success" | "error" | "info" | "warning" | "loading",
  content: string,
  duration?: number,
  onClose?: () => void
): void {
  message[level](content, duration, onClose);
}

/**
 * UIUtils 对象 - 统一导出所有工具函数
 */
export const UIUtils = {
  showDrawer,
  showModal,
  cleanupAllDrawers,
  getDrawerCount,
  msg,
};

export default UIUtils;
